import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FlightData, CrewMovementData, Station, CounterDistributionData, TimestampEntry, initialFlightData, NewsMessage, ScannedPassenger, ATSRData } from '../types';
import { useAuth } from './AuthContext';
import { database } from '../services/firebase';
import { ref, onValue, runTransaction, update, set, push, remove, serverTimestamp } from 'firebase/database';

interface ActiveFlights {
  [slotId: string]: FlightData;
}

const DEFAULT_NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRlIAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAAD/A/8EAf8EAgAEAf8EAf8D/wQCAAQCAAQB/wT/BAIBBP8E/wT/BP8D/wT/BAIEAgT/BP8E/wQCBQEEAgT/A/8E/wQCAAQCAgED/wT/BP8E/wQCBQEE/wP/A/8DAgQEAgT/BAIEAgT/BP8EAgQCAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wT/BAIAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIE";

const createDemoFlightData = (): FlightData => {
  const now = new Date();
  const etd = new Date(now);
  etd.setHours(20, 0, 0, 0);

  const timestamp = (minutesAgo: number) => ({
    time: new Date(now.getTime() - minutesAgo * 60 * 1000).toISOString(),
    by: 'DemoUser',
    modified: false,
  });

  return {
    ...initialFlightData,
    counterDistribution: {
      ...initialFlightData.counterDistribution,
      etd: etd.toISOString(),
      flightNo: 'JT-DEMO',
      booking: {
        c: '20',
        m: '150',
        inf: '5',
        totalPassengers: '175',
      },
      counters: {
        ...initialFlightData.counterDistribution.counters,
        'Counter 1': 'John D.',
        'Counter 2': 'Jane S.',
        'Counter 3': 'Peter F.',
      },
      gates: {
        ...initialFlightData.counterDistribution.gates,
        'Immigration': 'Imran K.',
        'Gate Control': 'Sara T.',
      },
    },
    crewMovement: {
      'Crew Arrived': { crew: [timestamp(60)], pilots: [timestamp(60)] },
      '1st X-ray': { crew: [timestamp(50)], pilots: [timestamp(50)] },
      'Immigration In': { crew: [timestamp(45)], pilots: [timestamp(45)] },
      'Immigration Out': { crew: [timestamp(40)], pilots: [timestamp(40)] },
      'At Gate': { crew: [timestamp(30)], pilots: [] },
      'At Aircraft': { crew: [], pilots: [timestamp(25)] },
    },
    immigrationApproved: true,
    immigrationComment: 'All crew documents verified.',
    newsFeed: {
      'demo1': { text: 'Welcome to the JT-Crew Movement Demo! Explore the features.', sender: 'Admin', timestamp: now.getTime() - 120000 },
      'demo2': { text: 'Ground staff, please prepare for gate assignment announcements.', sender: 'Supervisor', timestamp: now.getTime() - 60000 },
    },
    scannedPassengers: [
      { sequence: 1, name: 'DOE/JOHN', seatNumber: '14A', note: 'Scanned via camera', scannedAt: timestamp(10).time, scannedBy: 'DemoUser' },
      { sequence: 2, name: 'SMITH/JANE', seatNumber: '22C', note: 'Manually entered', scannedAt: timestamp(5).time, scannedBy: 'DemoUser' },
    ],
  };
};

interface FlightContextType {
  activeFlights: ActiveFlights;
  selectedFlightSlotId: string | null;
  selectFlight: (slotId: string) => void;
  selectedFlightData: FlightData | null;
  updateCrewMovement: (station: Station, type: 'crew' | 'pilots') => void;
  updateCounterDistributionField: (section: 'counters' | 'gates' | 'finalFigures' | 'root' | 'booking', key: string, value: string) => void;
  updateAtsrField: (key: keyof ATSRData, value: string) => void;
  updateFlightData: (key: keyof FlightData, value: any) => void;
  finalizeAcceptance: () => void;
  triggerSystemClear: () => void;
  triggerGateClear: () => void;
  saveCurrentFlight: (slotId: string) => Promise<{ success: boolean; message: string }>;
  startNewFlightSession: (flightNo: string, flightDate: string) => Promise<void>;
  endFlightSession: (slotId: string) => Promise<void>;
  lastReceived: number;
  isSending: boolean;
  areNotificationsEnabled: boolean;
  toggleNotifications: () => void;
  isAudioUnlocked: boolean;
  newsFeed: NewsMessage[];
  addNewsMessage: (message: string) => Promise<void>;
  clearNewsFeed: () => Promise<void>;
  addScannedPassenger: (passengerData: Omit<ScannedPassenger, 'sequence' | 'scannedAt' | 'scannedBy'>) => void;
  addCapturedDoc: (doc: { url: string; capturedAt: string; by: string }) => void;
  showNotification: (message: string, type?: 'success' | 'error') => void;
  notification: { message: string, type: 'success' | 'error' } | null;
  triggerNewFlightModal: boolean;
  startAndEndSession: (slotId: string) => Promise<void>;
  clearNewFlightModalTrigger: () => void;
  isLoading: boolean;
}

const FlightContext = createContext<FlightContextType | undefined>(undefined);

export const FlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeFlights, setActiveFlights] = useState<ActiveFlights>({});
  const [selectedFlightSlotId, setSelectedFlightSlotId] = useState<string | null>(() => localStorage.getItem('selectedFlightSlotId'));
  const [isLoading, setIsLoading] = useState(true);
  
  const [lastReceived, setLastReceived] = useState(Date.now());
  const [isSending, setIsSending] = useState(false);
  const { user, isMultiFlightEnabled, appAssets } = useAuth();
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);
  const notificationTimer = useRef<number | null>(null);
  const [triggerNewFlightModal, setTriggerNewFlightModal] = useState(false);
  
  // --- Audio State and Logic ---
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const [soundQueue, setSoundQueue] = useState<number[]>([]);
  const lastPlayedSoundTimestamp = useRef<number | null>(null);

  const [areNotificationsEnabled, setAreNotificationsEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('areNotificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  const showNotification = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    if (notificationTimer.current) {
      clearTimeout(notificationTimer.current);
    }
    notificationTimer.current = window.setTimeout(() => {
      setNotification(null);
    }, 3000);
  }, []);

  const playSoundNow = useCallback((timestamp: number) => {
    if (audioRef.current && areNotificationsEnabled) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.play()
        .then(() => {
          lastPlayedSoundTimestamp.current = timestamp;
        })
        .catch(e => {
          console.error("An unexpected audio error occurred during playback:", e);
          showNotification('Could not play notification sound.', 'error');
        });
    }
  }, [areNotificationsEnabled, showNotification]);

  const playSound = useCallback((timestamp: number) => {
    if (!areNotificationsEnabled) return;

    if (lastPlayedSoundTimestamp.current && Date.now() - lastPlayedSoundTimestamp.current < 1000) {
      return;
    }

    if (isAudioUnlocked) {
      playSoundNow(timestamp);
    } else {
      console.log('Audio has not been unlocked. Queuing sound.');
      setSoundQueue(prev => prev.includes(timestamp) ? prev : [...prev, timestamp]);
    }
  }, [areNotificationsEnabled, isAudioUnlocked, playSoundNow]);

  useEffect(() => {
    const unlockAudio = () => {
      if (isAudioUnlocked || !audioRef.current) {
        window.removeEventListener('pointerdown', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
        return;
      }
      
      console.log("Interaction detected, attempting to unlock audio...");
      audioRef.current.muted = true;
      const playPromise = audioRef.current.play();

      playPromise.then(() => {
        audioRef.current!.pause();
        audioRef.current!.currentTime = 0;
        audioRef.current!.muted = false;
        setIsAudioUnlocked(true);
        console.log("Audio context unlocked successfully!");
        window.removeEventListener('pointerdown', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
      }).catch(error => {
        console.warn('Audio unlock attempt failed. Will retry on next user interaction.', error);
      });
    };

    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, [isAudioUnlocked]);
  
  useEffect(() => {
    if (isAudioUnlocked && soundQueue.length > 0) {
      const latestTimestamp = soundQueue[soundQueue.length - 1];
      playSoundNow(latestTimestamp);
      setSoundQueue([]);
    }
  }, [isAudioUnlocked, soundQueue, playSoundNow]);


  const writeToDb = useCallback(async (writeFn: () => Promise<any>) => {
    setIsSending(true);
    try {
        await writeFn();
    } catch (error) {
        console.error("DB write error:", error);
        showNotification('An error occurred while saving data.', 'error');
        throw error;
    } finally {
        setIsSending(false);
    }
  }, [showNotification]);

  const selectFlight = useCallback((slotId: string) => {
    if (slotId) {
      localStorage.setItem('selectedFlightSlotId', slotId);
      setSelectedFlightSlotId(slotId);
    } else {
      localStorage.removeItem('selectedFlightSlotId');
      setSelectedFlightSlotId(null);
    }
  }, []);

  useEffect(() => {
    const activeFlightsRef = ref(database, 'activeFlights');
    const unsubscribe = onValue(activeFlightsRef, (snapshot) => {
        let data = snapshot.val();

        // --- DEMO LOGIC ---
        if ((!data || Object.keys(data).length === 0) && !sessionStorage.getItem('demoFlightCreated')) {
            console.log("No active flights found. Creating demo flight.");
            const demoFlight = createDemoFlightData();
            data = { 'slot_0': demoFlight };
            set(ref(database, 'activeFlights/slot_0'), demoFlight);
            sessionStorage.setItem('demoFlightCreated', 'true');
        }
        // --- END DEMO LOGIC ---
        
        setActiveFlights(data || {});
        
        const currentSlotId = localStorage.getItem('selectedFlightSlotId');
        if (data) {
            if (!currentSlotId || !data[currentSlotId]) {
                const firstSlotId = Object.keys(data)[0];
                selectFlight(firstSlotId || '');
            }
            
            const selectedData = currentSlotId ? data[currentSlotId] : null;
            if (selectedData && selectedData.lastNotification) {
                const remoteNotification = selectedData.lastNotification;
                if (remoteNotification.timestamp !== lastPlayedSoundTimestamp.current) {
                    if (remoteNotification.triggeredBy !== user?.username) {
                        playSound(remoteNotification.timestamp);
                    }
                }
            }

        } else {
            selectFlight('');
        }
        
        setLastReceived(Date.now());
        setIsLoading(false);
    });

    return () => unsubscribe();
  }, [selectFlight, user?.username, playSound]);

  const selectedFlightData = useMemo(() => {
    if (selectedFlightSlotId && activeFlights[selectedFlightSlotId]) {
      const flightData = activeFlights[selectedFlightSlotId];
      return {
        ...initialFlightData,
        ...flightData,
        crewMovement: flightData.crewMovement || {},
        counterDistribution: {
          ...initialFlightData.counterDistribution,
          ...(flightData.counterDistribution || {}),
          booking: { ...initialFlightData.counterDistribution.booking, ...(flightData.counterDistribution?.booking || {}) },
          counters: { ...initialFlightData.counterDistribution.counters, ...(flightData.counterDistribution?.counters || {}) },
          gates: { ...initialFlightData.counterDistribution.gates, ...(flightData.counterDistribution?.gates || {}) },
          finalFigures: { ...initialFlightData.counterDistribution.finalFigures, ...(flightData.counterDistribution?.finalFigures || {}) },
        },
        atsrData: { ...initialFlightData.atsrData, ...(flightData.atsrData || {}) },
        capturedDocs: flightData.capturedDocs || [],
        scannedPassengers: flightData.scannedPassengers || [],
        newsFeed: flightData.newsFeed || {},
        lastNotification: flightData.lastNotification || null,
      };
    }
    
    return null;
  }, [activeFlights, selectedFlightSlotId]);

  const newsFeed = useMemo((): NewsMessage[] => {
      if (!selectedFlightData || !selectedFlightData.newsFeed) return [];
      const newsFeedObj = selectedFlightData.newsFeed;
      
      return Object.entries(newsFeedObj)
          .filter((entry): entry is [string, Omit<NewsMessage, 'id'>] => {
            const value = entry[1];
            return typeof value === 'object' && value !== null;
          })
          .map(([id, value]) => ({ id, ...value }))
          .sort((a, b) => a.timestamp - b.timestamp);
  }, [selectedFlightData]);

  const toggleNotifications = useCallback(() => {
    setAreNotificationsEnabled(prev => {
        const newState = !prev;
        localStorage.setItem('areNotificationsEnabled', JSON.stringify(newState));
        return newState;
    });
  }, []);

  const updateCrewMovement = useCallback((station: Station, type: 'crew' | 'pilots') => {
    if (!selectedFlightSlotId || !selectedFlightData) return;
    const arrivalTime = new Date();
    
    // Play sound locally immediately for responsiveness
    playSound(Date.now());
    
    // This is the trigger for the backend (e.g., a Firebase Cloud Function) to send a push notification.
    const notificationStationsForPush: Station[] = ['Crew Arrived', 'Doors Closed'];
    if (notificationStationsForPush.includes(station)) {
        push(ref(database, 'pushNotificationQueue'), {
            flightNo: selectedFlightData.counterDistribution.flightNo || 'N/A',
            title: `Flight ${selectedFlightData.counterDistribution.flightNo || 'N/A'} Update`,
            body: `${station} recorded by ${user?.username || 'Unknown'}.`,
            timestamp: serverTimestamp()
        });
    }

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}`), (currentData) => {
      if (currentData === null) return;

      const newEntry: Omit<TimestampEntry, 'modified'> = {
        time: arrivalTime.toISOString(),
        by: user?.username || 'Unknown',
      };

      const updateStation = (data: FlightData, updateType: 'crew' | 'pilots') => {
        const crewMovement = data.crewMovement || {};
        const stationTimestamps = crewMovement[station] || { crew: [], pilots: [] };
        const existingTimestamps = stationTimestamps[updateType] || [];
        
        const entryToAdd: TimestampEntry = { ...newEntry, modified: existingTimestamps.length > 0 };

        const updatedTimestamps = {
          ...stationTimestamps,
          [updateType]: [...existingTimestamps, entryToAdd],
        };
        
        data.crewMovement = { ...crewMovement, [station]: updatedTimestamps };
      };

      if (currentData.crewAndPilotsArrivedTogether) {
        updateStation(currentData, 'crew');
        updateStation(currentData, 'pilots');
      } else {
        updateStation(currentData, type);
      }
      
      const notificationStations: Station[] = ['Crew Arrived', '1st X-ray', '1st Pax IN', 'Doors Closed'];
      if (notificationStations.includes(station)) {
          currentData.lastNotification = {
              station: station,
              time: arrivalTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
              timestamp: Date.now(),
              triggeredBy: user?.username || 'Unknown',
          };
      }
      
      return currentData;
    }));
  }, [user, writeToDb, selectedFlightSlotId, playSound, selectedFlightData]);

  const updateCounterDistributionField = useCallback((section: 'counters' | 'gates' | 'finalFigures' | 'root' | 'booking', key: string, value: string) => {
    if (!selectedFlightSlotId) return;
    const updates: { [key: string]: any } = {};
    if (section === 'root') {
      updates[`/activeFlights/${selectedFlightSlotId}/counterDistribution/${key}`] = value;
    } else {
      updates[`/activeFlights/${selectedFlightSlotId}/counterDistribution/${section}/${key}`] = value;
    }
    writeToDb(() => update(ref(database), updates));
  }, [writeToDb, selectedFlightSlotId]);
  
  const updateAtsrField = useCallback((key: keyof ATSRData, value: string) => {
    if (!selectedFlightSlotId) return;
    const updates: { [key: string]: any } = {};
    updates[`/activeFlights/${selectedFlightSlotId}/atsrData/${key}`] = value;
    writeToDb(() => update(ref(database), updates));
  }, [writeToDb, selectedFlightSlotId]);
  
  const updateFlightData = useCallback((key: keyof FlightData, value: any) => {
    if (!selectedFlightSlotId) return;
    const updates: { [key: string]: any } = {};
    updates[`/activeFlights/${selectedFlightSlotId}/${key}`] = value;
    writeToDb(() => update(ref(database), updates));
  }, [writeToDb, selectedFlightSlotId]);

  const finalizeAcceptance = useCallback(() => {
    if (!selectedFlightSlotId || !user || !selectedFlightData) return;
    
    const acceptanceTime = new Date();

    // Trigger for push notification
    push(ref(database, 'pushNotificationQueue'), {
        flightNo: selectedFlightData.counterDistribution.flightNo || 'N/A',
        title: `Flight ${selectedFlightData.counterDistribution.flightNo || 'N/A'} Finalized`,
        body: `Acceptance Finalized by ${user.username}.`,
        timestamp: serverTimestamp()
    });

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}`), (currentData) => {
        if (currentData === null) return;

        currentData.acceptanceFinalized = {
            time: acceptanceTime.toISOString(),
            by: user.username,
        };

        currentData.lastNotification = {
            station: 'Acceptance Finalized',
            time: acceptanceTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
            timestamp: Date.now(),
            triggeredBy: user.username,
        };

        return currentData;
    }));

    // Play sound locally
    playSound(Date.now());
    
  }, [user, writeToDb, selectedFlightSlotId, playSound, selectedFlightData]);

  const triggerSystemClear = useCallback(() => {
    if (!selectedFlightSlotId || !user) return;
    
    const clearTime = new Date();

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}`), (currentData) => {
        if (currentData === null) return;

        currentData.systemClear = {
            time: clearTime.toISOString(),
            by: user.username,
        };

        currentData.lastNotification = {
            station: 'System Clear',
            time: clearTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
            timestamp: Date.now(),
            triggeredBy: user.username,
        };

        return currentData;
    }));

    playSound(Date.now());
  }, [user, writeToDb, selectedFlightSlotId, playSound]);

  const triggerGateClear = useCallback(() => {
      if (!selectedFlightSlotId || !user) return;
    
    const clearTime = new Date();

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}`), (currentData) => {
        if (currentData === null) return;

        currentData.gateClear = {
            time: clearTime.toISOString(),
            by: user.username,
        };

        currentData.lastNotification = {
            station: 'Gate Clear',
            time: clearTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
            timestamp: Date.now(),
            triggeredBy: user.username,
        };

        return currentData;
    }));

    playSound(Date.now());
  }, [user, writeToDb, selectedFlightSlotId, playSound]);

  const saveCurrentFlight = useCallback(async (slotId: string): Promise<{ success: boolean; message: string }> => {
    const flightToSave = activeFlights[slotId];
    if (!flightToSave || !flightToSave.counterDistribution.flightNo) {
      return { success: false, message: "Cannot create report: Flight Number is missing." };
    }

    const { flightNo } = flightToSave.counterDistribution;
    const now = new Date();
    const flightDate = now.toISOString().split('T')[0];
    const flightId = `${flightNo}-${now.getTime()}`;
    const record = { ...flightToSave, id: flightId, flightDate };
    
    try {
        await writeToDb(() => set(ref(database, `flightRecords/${flightId}`), record));
        return { success: true, message: `Report for flight ${flightNo} created successfully.` };
    } catch (error) {
        console.error("Error saving flight record:", error);
        return { success: false, message: "An error occurred while saving the report." };
    }
  }, [activeFlights, writeToDb]);

  const endFlightSession = useCallback(async (slotId: string): Promise<void> => {
    try {
      const result = await saveCurrentFlight(slotId);
      if (result.success) {
        await writeToDb(() => remove(ref(database, `activeFlights/${slotId}`)));
        showNotification(result.message, 'success');
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error("Error ending flight session:", error);
      showNotification("An error occurred while ending the session.", 'error');
      throw error;
    }
  }, [saveCurrentFlight, writeToDb, showNotification]);
  
  const startNewFlightSession = useCallback(async (flightNo: string, flightDate: string): Promise<void> => {
    const existingSlots = Object.keys(activeFlights);
    const maxFlights = isMultiFlightEnabled ? 3 : 1;
    if (existingSlots.length >= maxFlights) {
      showNotification(`Cannot start new flight: Maximum of ${maxFlights} active flight(s) reached.`, "error");
      return;
    }

    let newSlotId: string | null = null;
    if (isMultiFlightEnabled) {
        for (let i = 0; i < 3; i++) {
            const potentialSlotId = `slot_${i}`;
            if (!existingSlots.includes(potentialSlotId)) {
                newSlotId = potentialSlotId;
                break;
            }
        }
    } else {
        newSlotId = 'slot_0';
    }

    if (!newSlotId) {
        showNotification("Error finding an available flight slot.", "error");
        return;
    }

    const newFlightState: FlightData = {
      ...initialFlightData,
      scannedPassengers: [],
      counterDistribution: {
        ...initialFlightData.counterDistribution,
        flightNo: flightNo,
        etd: `${flightDate}T00:00`,
      },
    };

    try {
      await writeToDb(() => set(ref(database, `activeFlights/${newSlotId}`), newFlightState));
      selectFlight(newSlotId);
    } catch (error) {
      console.error("Error starting new flight report:", error);
      throw error;
    }
  }, [writeToDb, activeFlights, showNotification, selectFlight, isMultiFlightEnabled]);

  const addNewsMessage = useCallback(async (message: string): Promise<void> => {
    if (!message.trim() || !user || !selectedFlightSlotId || !selectedFlightData) return;
    const newMessage: Omit<NewsMessage, 'id'> = {
        text: message.trim(),
        sender: user.username,
        timestamp: Date.now()
    };
    
    // Trigger for push notification on news message
    push(ref(database, 'pushNotificationQueue'), {
        flightNo: selectedFlightData.counterDistribution.flightNo || 'N/A',
        title: `Announcement by ${user.username}`,
        body: message.trim(),
        timestamp: serverTimestamp()
    });

    await writeToDb(() => push(ref(database, `activeFlights/${selectedFlightSlotId}/newsFeed`), newMessage));
  }, [user, writeToDb, selectedFlightSlotId, selectedFlightData]);

  const clearNewsFeed = useCallback(async (): Promise<void> => {
    if (!selectedFlightSlotId) return;
    await writeToDb(() => remove(ref(database, `activeFlights/${selectedFlightSlotId}/newsFeed`)));
  }, [writeToDb, selectedFlightSlotId]);

  const addCapturedDoc = useCallback((doc: { url: string; capturedAt: string; by: string }) => {
    if (!selectedFlightSlotId) return;
    const docsRef = ref(database, `activeFlights/${selectedFlightSlotId}/capturedDocs`);
    writeToDb(() => runTransaction(docsRef, (currentDocs) => {
      if (currentDocs === null) {
        currentDocs = [];
      }
      currentDocs.push(doc);
      return currentDocs;
    }));
  }, [writeToDb, selectedFlightSlotId]);

  const addScannedPassenger = useCallback((passengerData: Omit<ScannedPassenger, 'sequence' | 'scannedAt' | 'scannedBy'>) => {
    if (!user || !selectedFlightSlotId) return;

    const passengersRef = ref(database, `activeFlights/${selectedFlightSlotId}/scannedPassengers`);
    writeToDb(() => runTransaction(passengersRef, (currentPassengers) => {
      if (currentPassengers === null) {
        currentPassengers = [];
      }
      const newPassenger: ScannedPassenger = {
        ...passengerData,
        sequence: currentPassengers.length + 1,
        scannedAt: new Date().toISOString(),
        scannedBy: user.username,
      };
      currentPassengers.push(newPassenger);
      return currentPassengers;
    }));
  }, [user, writeToDb, selectedFlightSlotId]);
  
  const startAndEndSession = useCallback(async (slotId: string): Promise<void> => {
    await endFlightSession(slotId);
    setTriggerNewFlightModal(true);
  }, [endFlightSession]);

  const clearNewFlightModalTrigger = useCallback(() => {
    setTriggerNewFlightModal(false);
  }, []);

  const contextValue = useMemo(() => ({
    activeFlights,
    selectedFlightSlotId,
    selectFlight,
    selectedFlightData,
    updateCrewMovement,
    updateCounterDistributionField,
    updateAtsrField,
    updateFlightData,
    finalizeAcceptance,
    triggerSystemClear,
    triggerGateClear,
    saveCurrentFlight,
    startNewFlightSession,
    endFlightSession,
    lastReceived,
    isSending,
    areNotificationsEnabled,
    toggleNotifications,
    isAudioUnlocked,
    newsFeed,
    addNewsMessage,
    clearNewsFeed,
    addScannedPassenger,
    addCapturedDoc,
    notification,
    showNotification,
    triggerNewFlightModal,
    startAndEndSession,
    clearNewFlightModalTrigger,
    isLoading
  }), [
    activeFlights,
    selectedFlightSlotId,
    selectFlight,
    selectedFlightData,
    updateCrewMovement,
    updateCounterDistributionField,
    updateAtsrField,
    updateFlightData,
    finalizeAcceptance,
    triggerSystemClear,
    triggerGateClear,
    saveCurrentFlight,
    startNewFlightSession,
    endFlightSession,
    lastReceived,
    isSending,
    areNotificationsEnabled,
    toggleNotifications,
    isAudioUnlocked,
    newsFeed,
    addNewsMessage,
    clearNewsFeed,
    addScannedPassenger,
    addCapturedDoc,
    notification,
    showNotification,
    triggerNewFlightModal,
    startAndEndSession,
    clearNewFlightModalTrigger,
    isLoading
  ]);

  return (
    <FlightContext.Provider value={contextValue}>
      <>
        {children}
        <audio
            ref={audioRef}
            src={appAssets.notificationSound || DEFAULT_NOTIFICATION_SOUND}
            key={appAssets.notificationSound}
        />
      </>
    </FlightContext.Provider>
  );
};

export const useFlight = (): FlightContextType => {
  const context = useContext(FlightContext);
  if (context === undefined) {
    throw new Error('useFlight must be used within a FlightProvider');
  }
  return context;
};